import React, { useContext } from 'react';
import { Box, Button, useTheme } from '@mui/material';
import { useDocumentPreviewContext } from '@europrocurement/l2d-modules/modules/Dossiers/providers/DocumentPreviewContext';
import { useDossierDocumentFilesApi } from '@europrocurement/l2d-modules/modules/Dossiers/hooks';
import { SelectedDossierContext } from '@europrocurement/l2d-modules/modules/Dossiers/providers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileDashedLine, faRefresh, faSparkles } from '@fortawesome/pro-regular-svg-icons';
import {
    ADD_DOS_DOC_CTX,
    STATUS_REFUSED,
    STATUS_VALID,
    useDossierDocumentsCtx,
    useDossierDocumentsCtxDispatch,
} from '@europrocurement/l2d-modules/providers';
import DossierDocumentsValidationDocumentName from '../DossierDocumentsValidationDocumentName/DossierDocumentsValidationDocumentName';
import DossierDocumentsValidationDocumentStatus from '../DossierDocumentsValidationDocumentStatus/DossierDocumentsValidationDocumentStatus';
import DossierDocumentValidationDocumentType from '../../DossierDocumentValidationDocumentType/DossierDocumentValidationDocumentType';

export type DossierDocumentsValidationHeaderProps = {
    onDocumentTypeChange: (fichierId: number, typeDocId: number) => void;
    onDocumentStatusChange: (fichierId: number, statusId: number) => void;
    onDocumentAutoType: (fichierId: number) => void;
    onDocumentDeliassage: (fichierId: number) => void;
    onDocumentRefresh?: (dossierIds: number[]) => void;
    isSubmitting: boolean;
    navigationBtn?: React.JSX.Element;
};

const DossierDocumentsValidationHeader: React.FunctionComponent<DossierDocumentsValidationHeaderProps> =
    function (props) {
        const {
            onDocumentTypeChange,
            onDocumentStatusChange,
            onDocumentDeliassage,
            onDocumentAutoType,
            onDocumentRefresh,
            isSubmitting,
            navigationBtn,
        } = props;
        const { getColorByStatus, getFileIcon, postDosDoc } = useDossierDocumentFilesApi();
        const { dossier } = useContext(SelectedDossierContext);
        const dossierDocumentsDispatch = useDossierDocumentsCtxDispatch();
        const { getStatusIdentifier, getDossierDocuments } = useDossierDocumentsCtx();
        const { documentPreview } = useDocumentPreviewContext();
        const { palette } = useTheme();
        const documentColor = documentPreview?.statusId
            ? getColorByStatus(getStatusIdentifier(documentPreview?.statusId))
            : 'primary';
        const documentIcon = documentPreview?.fileType
            ? getFileIcon(documentPreview?.fileType)
            : 'file';

        const handleAccept = () => {
            if (!documentPreview?.fichierId) {
                return;
            }

            onDocumentStatusChange(documentPreview.fichierId as number, STATUS_VALID);
        };

        const handleDeny = () => {
            if (!documentPreview?.fichierId) {
                return;
            }

            onDocumentStatusChange(documentPreview.fichierId as number, STATUS_REFUSED);
        };

        return (
            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    padding: '15px 16px',
                    borderBottomColor: palette.grey[200],
                    borderBottomWidth: '1px',
                    borderBottomStyle: 'solid',
                }}
            >
                {navigationBtn}
                {documentPreview && (
                    <DossierDocumentsValidationDocumentName
                        fileIcon={documentIcon}
                        documentColor={documentColor}
                        label={documentPreview.documentLabel as string}
                        fileName={documentPreview.fileName as string}
                    />
                )}
                {documentPreview && (
                    <DossierDocumentValidationDocumentType
                        isSubmitting={isSubmitting}
                        label={documentPreview.documentLabel}
                        typeDocType={documentPreview.expediteurType}
                        onDocumentTypeChange={async (newDocType) => {
                            if (!newDocType) {
                                return;
                            }

                            // Check if this kind of dosDocs already exists in the dossier
                            // And do not have a file linked
                            const availableEmptyDosDocWithSameType = getDossierDocuments().find(
                                (doc) =>
                                    doc.dossierId === documentPreview.dossierId &&
                                    doc.expediteurTypeId === documentPreview.expediteurType &&
                                    doc.documentTypeId === newDocType.id &&
                                    doc.fichierId === undefined,
                            );

                            // This type of document do not extist in dossier
                            if (!availableEmptyDosDocWithSameType) {
                                // Add a dosDoc to the dossier
                                // Link document type to the dossier
                                const newDosDoc = await postDosDoc(
                                    dossier.id as number,
                                    newDocType.id as number,
                                    newDocType.label as string,
                                    dossier.statutId as number,
                                );

                                // Maj document in context
                                dossierDocumentsDispatch({
                                    type: ADD_DOS_DOC_CTX,
                                    payload: newDosDoc,
                                });

                                onDocumentTypeChange(
                                    documentPreview.fichierId as number,
                                    newDosDoc.id as number,
                                );
                            } else {
                                onDocumentTypeChange(
                                    documentPreview.fichierId as number,
                                    availableEmptyDosDocWithSameType.id,
                                );
                            }
                        }}
                    />
                )}
                <Box
                    sx={{
                        display: 'flex',
                        gap: '0 8px',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        marginRight: '0',
                    }}
                >
                    {documentPreview && (
                        <DossierDocumentsValidationDocumentStatus
                            statusId={documentPreview?.statusId as number}
                            onAcceptDocument={handleAccept}
                            onDenyDocument={handleDeny}
                            isSubmitting={isSubmitting}
                        />
                    )}

                    {documentPreview?.fichierId && documentPreview.fichierId && (
                        <Button
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0 8px',
                                color: palette.primary.dark,
                                ':hover': {
                                    color: palette.primary.main,
                                },
                            }}
                            disabled={isSubmitting}
                            onClick={() => onDocumentAutoType(documentPreview.fichierId as number)}
                        >
                            <FontAwesomeIcon
                                icon={faSparkles}
                                fontSize="18px"
                            />
                            Typer
                        </Button>
                    )}
                    {documentPreview?.fichierId && documentPreview.fichierId && (
                        <Button
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0 8px',
                                color: palette.primary.dark,
                                ':hover': {
                                    color: palette.primary.main,
                                },
                            }}
                            disabled={isSubmitting}
                            onClick={() =>
                                onDocumentDeliassage(documentPreview.fichierId as number)
                            }
                        >
                            <FontAwesomeIcon
                                icon={faFileDashedLine}
                                fontSize="18px"
                            />
                            Déliasser
                        </Button>
                    )}
                    {onDocumentRefresh && typeof onDocumentRefresh !== 'undefined' && (
                        <Button
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0 8px',
                                color: palette.primary.dark,
                                ':hover': {
                                    color: palette.primary.main,
                                },
                            }}
                            onClick={() => onDocumentRefresh([dossier.id as number])}
                        >
                            <FontAwesomeIcon
                                icon={faRefresh}
                                fontSize="18px"
                            />
                        </Button>
                    )}
                </Box>
            </Box>
        );
    };

export default DossierDocumentsValidationHeader;
